/**
 * 🔴 **页面网格 —— 设计的 `.grid / .g2 / .g3 / .g4` 在客户端的**唯一实现**（O 域 · `AC-O1.10` 4pt 栅格）
 *
 * ## 为什么需要它（⑳→㉜ 两批留下的最大视觉偏差）
 *
 * 骨架已经按设计改正为「侧栏 228px + 顶栏 58px + 内容区 1320px」，
 * 但**内容区内部**每一页仍是**单列 `Stack` 堆叠** —— 在 1320px 的宽屏上，
 * 卡片拉满 1320px 宽、行高被拉长，视觉上"偏空"，
 * 与设计资产 `assets/app.css:701-704` 的四套网格**完全不是一回事**。
 *
 * ## 🔴 三条纪律
 *
 * | # | 纪律 | 做法 |
 * |---|---|---|
 * | ① | **断点只有这一处** | 设计的三个媒体查询（`app.css:737-750`）逐字搬过来；<br>页面**不得**自己写 `gridTemplateColumns` 的媒体查询（那就是第二处真相） |
 * | ② | **间距只走令牌** | `gap` 取**生成产物**里的 `'gap-grid'`（= `clamp(12px, 1.2vw, 18px)`）—— 不写 `16px`；<br>同理 `rowGap` 可覆盖，但默认同 `gap` |
 * | ③ | **不引入 MUI `<Grid>`** | MUI 的 `<Grid>` 是 **12 栅格 + `spacing` 主题倍率**，与设计的 `repeat(n, minmax(0,1fr))`<br>不是同一套模型；套一层转换 = 多一份映射表。⇒ 用 `Box` + CSS Grid，**语义与设计一一对应** |
 *
 * ## 🔴 降列规则（照抄设计，勿"优化"）
 *
 * | 视口 | 变化 |
 * |---|---|
 * | `> 1200px` | `.g4` 四列 · `.g3` 三列 · `.g2` 两列 |
 * | `≤ 1200px` | `.g4` → **两列**（四列在 1200px 下每列不足 280px，数字会折行） |
 * | `≤ 1024px` | 侧栏收 64px（`Shell.tsx` 的 `COLLAPSED`）· `.g3` → **两列** |
 * | `≤ 720px`  | `.g2 / .g3 / .g4` **全部单列** |
 *
 * ⚠️ **`≤1024px` 那一档同时改侧栏和 `.g3`** —— 设计把它们写在同一个媒体查询里，
 * 本文件**照抄**（分两处写就等于允许它们漂移）。
 */

import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { TOKENS_LIGHT } from '../theme/tokens.generated.js';

/**
 * 🔴 三个断点 —— 与 `app.css:737/742/748` 逐字一致；`Shell.tsx` 的 `COLLAPSED` 也是 1024px。
 *
 * 🆕 **导出**（不只内部用）：让 `Grid.spec.tsx` 能**断言"用的是设计的那三个断点"**。
 * 若它们只是私有常量，断点被改成 `1100px` 时没有任何断言会红 ——
 * 而那正是"客户端与设计悄悄分叉"的起点（铁律 10：双端同源）。
 */
export const BP_1200 = '@media (max-width: 1200px)';
export const BP_1024 = '@media (max-width: 1024px)';
export const BP_720 = '@media (max-width: 720px)';

/** 列数 = 设计的三档命名（`.g2/.g3/.g4`）。**不提供 1 列** —— 单列请直接不用 `<Grid>`。 */
export type GridCols = 2 | 3 | 4;

/**
 * 按视口宽度推导列数 —— **降列规则的唯一实现**。
 *
 * 抽成纯函数有两个理由：
 * 1. **可测**：断点逻辑若写在 `sx` 里，单测只能证"有媒体查询字符串"，证不了"1024px 时真的是两列"；
 *    而 `colsAt(1024)` 是一条可断言的值（见 `Grid.spec.tsx`）。
 * 2. **无头取证可复用**：几何取证脚本要预测"这里应该有几列"，
 *    用**同一个函数**预测，而不是在脚本里再抄一遍断点。
 */
export function colsAt(cols: GridCols, width: number): number {
  if (width <= 720) return 1;
  if (width <= 1024) return cols === 3 ? 2 : cols === 4 ? 2 : cols;
  if (width <= 1200) return cols === 4 ? 2 : cols;
  return cols;
}

function template(cols: number): string {
  return `repeat(${cols}, minmax(0, 1fr))`;
}

export interface GridProps {
  /**
   * 设计档位（`.g2` / `.g3` / `.g4`）。
   * 🔴 给 `templateColumns` 时**可省略** —— 那一档由自定义模板自己说话。
   */
  cols?: GridCols;
  children: ReactNode;
  /**
   * 🆕 **自定义列模板** —— 逃生舱，用于设计里那几处**非等宽**网格
   * （如首页 `minmax(0,1.62fr) minmax(0,1fr)` 的主/侧栏，**行内 style** 写在
   * `01-今天_原型.html:454`；`记一笔` 的 `minmax(0,1fr) 300px` 在 `03-记一笔_原型.html`）。
   *
   * ⚠️ 给了它就**不参与 1200/1024 两档降列**（自定义模板的每列宽度是内容相关的，
   * 端侧无法安全地"降"它）⇒ 使用者必须自己在需要时给窄屏兜底。
   * ✅ 但 `≤720px` **一律降单列**（依据见下方实现的注释）。
   * 传了就不要同时传 `cols`（`cols` 会被忽略 —— 显式覆盖优先，且这里不报错：
   * 两个意图同时出现时，静默丢弃一个比抛错更难查）。
   */
  templateColumns?: string;
  /** 🔴 `minmax(0,1fr)` 的 `min` 已含；子项要**跨列**时用 `span`（如设计的 `grid-column: span 2`） */
  sx?: Record<string, unknown>;
  /** 语义标签（`section` / `div` / …）—— 网格本身不该被当成无义的 `div` 堆 */
  component?: 'div' | 'section' | 'ul';
  'data-testid'?: string;
}

/**
 * 页面网格。
 *
 * ```tsx
 * <Grid cols={4}>
 *   <StatCard />…   // 4 列 → ≤1200px 两列 → ≤720px 单列
 * </Grid>
 * ```
 */
export function Grid({ cols, children, templateColumns, sx, component = 'div', ...rest }: GridProps) {
  // 🔴 两条路径**互斥且都要有交代**：`templateColumns` 优先（自定义模板自带列数），
  //    否则用 `cols` 的降列规则。两者都缺 = 调用方没想清楚 ⇒ 抛错而不是静默单列。
  if (cols === undefined && templateColumns === undefined) {
    throw new Error('<Grid> 必须给 cols（设计档位 .g2/.g3/.g4）或 templateColumns（自定义模板）');
  }
  // 🔴 显式窄化给 TS 看（`throw` 之后 `cols` 仍可能是 `undefined`，因为上面那行只保证
  //    「两者不同时缺」）。这里重建成一个**局部常量**，后面的降列分支不必再判空。
  const c: GridCols | undefined = cols;

  return (
    <Box
      component={component}
      data-grid={cols ?? 'custom'}
      sx={{
        display: 'grid',
        // 🔴 间距**从生成产物取值**，不写死也不写 `var(--gap-grid)`。
        //
        //    为什么不是 `var(--gap-grid)`（无头取证 2026-10-04 实测推翻的写法）：
        //    客户端**没有加载** `tokens.css`（`index.html` / `main.tsx` 都不引它 —— Web 端
        //    的令牌是**构建期**经 `gen-tokens` 转成 `tokens.generated.ts` 的，铁律 10 的
        //    "构建期生成"就是为此）。⇒ 那个 CSS 变量在浏览器里**根本不存在**，
        //    `var(--gap-grid)` 解析失败 ⇒ `gap` 静默退回 `normal`（= 0）。
        //    实测证据：无头 CDP 读到每一档视口的 `gap` 都是 `normal`，不是 `clamp(...)` 算出的值。
        //
        //    ⇒ 从 `TOKENS_LIGHT` 读，既守住"令牌单一来源"，又**真的在运行时生效**。
        //    （暗色下 `gap-grid` 两套取值相同 —— 它是纯尺寸，不随主题变。）
        gap: TOKENS_LIGHT['gap-grid'],
        ...(templateColumns === undefined
          ? {
              gridTemplateColumns: template(c as GridCols),
              // ↓ 照抄设计的降列（顺序不可换：窄的在前，先命中先生效）
              [BP_1200]: c === 4 ? { gridTemplateColumns: template(2) } : {},
              [BP_1024]: c === 3 ? { gridTemplateColumns: template(2) } : {},
              [BP_720]: { gridTemplateColumns: template(1) },
            }
          : {
              gridTemplateColumns: templateColumns,
              // 🔴 自定义模板**在 ≤720px 仍降单列**（无头取证 2026-10-04 实测补上的）。
              //    之前注释写的是"不响应降列，使用者自己兜底" —— 那是**错的**：
              //    720px 下 1.62fr 的主栏只剩 ~386px，图表被压扁，视觉上就是坏的
              //    （实测那档首页 `templateColumns` 仍是 `385.8px 238.2px` 两列）。
              //
              //    ⚠️ 依据说明（别当成"照抄"）：`app.css:748` 原文只写了 `.g2, .g3, .g4`，
              //    **不含**行内 style 的自定义模板；但它同一条媒体查询里还有
              //    `.search-trigger { flex: 1 }`，可见**这一档是"窄屏兜底"而非"网格专属"**。
              //    且 `≤720px` 在 Web 端已无实际用户（底栏形态才用得到，见 ㉜），
              //    单列是最保守的退化。⇒ 这是**有依据的判断**，不是照抄。
              [BP_720]: { gridTemplateColumns: template(1) },
            }),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** 子项跨列（设计的 `grid-column: span 2`）；`≤720px` 自动收回单列（跨列在单列里无意义） */
export function GridSpan({ span, children, sx }: { span: number; children: ReactNode; sx?: Record<string, unknown> }) {
  return (
    <Box sx={{ gridColumn: `span ${span}`, minWidth: 0, [BP_720]: { gridColumn: 'auto' }, ...sx }}>{children}</Box>
  );
}
